import {
  useIsRestoring,
  useQuery,
  type QueryClient,
} from "@tanstack/react-query";
import { randomUUID } from "expo-crypto";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type { List, Task, TaskAlert, TaskCompletion } from "~/api/types";
import { useAuth } from "~/hooks/useAuth";

export type { List, Task, TaskAlert, TaskCompletion };

/** Rows are created locally with a client id so a task added offline (or as a
 * guest) keeps the same identity once it reaches the API. */
export const generateId = () => randomUUID();

export type EntityName = "lists" | "tasks" | "task_completions" | "task_alerts";

/** A guest's rows use the `"guest"` scope; a signed-in user's use their id. The
 * cache is persisted and a device can be shared, so scoping the key keeps one
 * user's rows from ever restoring under another's. */
export const GUEST = "guest" as const;
export const scopeKey = (entity: EntityName, scope: string) =>
  [entity, scope] as const;

// --- request bodies -------------------------------------------------------
// Rows carry server-managed columns (owner_id, timestamps) the API ignores, so
// each write sends only the fields the endpoint accepts.

export const listBody = (row: Partial<List>) => ({
  name: row.name as string,
  color: row.color,
  archived: row.archived,
  pinned: row.pinned,
  deleted: row.deleted,
});

export const taskBody = (row: Partial<Task>) => ({
  list_id: row.list_id as string,
  title: row.title as string,
  notes: row.notes,
  line_type: row.line_type,
  cadence: row.cadence,
  weekday: row.weekday,
  month_day: row.month_day,
  due_on: row.due_on,
  completed_at: row.completed_at,
  sort_order: row.sort_order,
  reminder_time: row.reminder_time,
  last_reminder_sent_on: row.last_reminder_sent_on,
  deleted: row.deleted,
});

export const completionBody = (row: Partial<TaskCompletion>) => ({
  task_id: row.task_id as string,
  completed_on: row.completed_on as string,
  deleted: row.deleted,
});

export const alertBody = (row: Partial<TaskAlert>) => ({
  list_id: row.list_id,
  task_id: row.task_id,
  kind: row.kind as TaskAlert["kind"],
  hour_local: row.hour_local,
  enabled: row.enabled,
  deleted: row.deleted,
});

/** Creates or replaces one row by its client id (idempotent, safe to replay). */
export async function putRow(entity: EntityName, row: Record<string, unknown>) {
  const path = { id: row.id as string };
  switch (entity) {
    case "lists":
      return unwrap(
        api.putList({ path, body: listBody(row as Partial<List>) }),
      );
    case "tasks":
      return unwrap(
        api.putTask({ path, body: taskBody(row as Partial<Task>) }),
      );
    case "task_completions":
      return unwrap(
        api.putTaskCompletion({
          path,
          body: completionBody(row as Partial<TaskCompletion>),
        }),
      );
    case "task_alerts":
      return unwrap(
        api.putTaskAlert({ path, body: alertBody(row as Partial<TaskAlert>) }),
      );
  }
}

/** Changes some fields of an existing row. */
export async function patchRow(
  entity: EntityName,
  id: string,
  fields: Record<string, unknown>,
) {
  const path = { id };
  switch (entity) {
    case "lists":
      return unwrap(
        api.patchList({ path, body: listBody(fields as Partial<List>) }),
      );
    case "tasks":
      return unwrap(
        api.patchTask({ path, body: taskBody(fields as Partial<Task>) }),
      );
    case "task_completions":
      return unwrap(
        api.patchTaskCompletion({
          path,
          body: completionBody(fields as Partial<TaskCompletion>),
        }),
      );
    case "task_alerts":
      return unwrap(
        api.patchTaskAlert({
          path,
          body: alertBody(fields as Partial<TaskAlert>),
        }),
      );
  }
}

async function fetchOwned<T>(entity: EntityName): Promise<T[]> {
  switch (entity) {
    case "lists":
      return (await unwrap(api.listLists())).items as T[];
    case "tasks":
      return (await unwrap(api.listTasks())).items as T[];
    case "task_completions":
      return (await unwrap(api.listTaskCompletions())).items as T[];
    case "task_alerts":
      return (await unwrap(api.listTaskAlerts())).items as T[];
  }
}

/** Fetches a signed-in user's lists straight from the API, bypassing
 * useListsData()'s cache -- for callers (like onboarding) that need to know
 * the starter list actually exists rather than trust a query that may not
 * have loaded yet. */
export function fetchLists(_userId: string) {
  return fetchOwned<List>("lists");
}

export function useOwnerId() {
  const { user } = useAuth();
  return user?.id ?? null;
}

/** The right rows for the current session — the guest cache when signed out,
 * the synced Postgres rows when signed in. */
function useEntity<T>(entity: EntityName) {
  const userId = useOwnerId();
  const isRestoring = useIsRestoring();

  const guestQuery = useQuery({
    queryKey: scopeKey(entity, GUEST),
    queryFn: () => [] as T[],
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !isRestoring && !userId,
  });

  const authedQuery = useQuery({
    queryKey: scopeKey(entity, userId ?? ""),
    queryFn: () => fetchOwned<T>(entity),
    enabled: Boolean(userId),
  });

  return userId ? authedQuery : guestQuery;
}

export const useListsData = () => useEntity<List>("lists");
export const useTasksData = () => useEntity<Task>("tasks");
export const useCompletionsData = () =>
  useEntity<TaskCompletion>("task_completions");
export const useAlertsData = () => useEntity<TaskAlert>("task_alerts");

// --- guest cache writes (no server) ------------------------------------

type WithId = { id: string };

export function writeGuest<T extends WithId>(
  queryClient: QueryClient,
  entity: EntityName,
  updater: (list: T[]) => T[],
) {
  queryClient.setQueryData<T[]>(scopeKey(entity, GUEST), (current) =>
    updater(current ?? []),
  );
}

// --- optimistic cache helpers, shared by the signed-in mutation defaults ---

export function cacheUpsert<T extends WithId>(
  queryClient: QueryClient,
  entity: EntityName,
  scope: string,
  row: T,
) {
  queryClient.setQueryData<T[]>(scopeKey(entity, scope), (current) => {
    const list = current ?? [];
    const index = list.findIndex((item) => item.id === row.id);
    if (index === -1) return [...list, row];
    const next = list.slice();
    next[index] = { ...next[index], ...row };
    return next;
  });
}

export function cachePatch<T extends WithId>(
  queryClient: QueryClient,
  entity: EntityName,
  scope: string,
  id: string,
  patch: Partial<T>,
) {
  queryClient.setQueryData<T[]>(scopeKey(entity, scope), (current) =>
    (current ?? []).map((item) =>
      item.id === id ? { ...item, ...patch } : item,
    ),
  );
}

export function cacheRemove<T extends WithId>(
  queryClient: QueryClient,
  entity: EntityName,
  scope: string,
  predicate: (row: T) => boolean,
) {
  queryClient.setQueryData<T[]>(scopeKey(entity, scope), (current) =>
    (current ?? []).filter((item) => !predicate(item)),
  );
}

const ALL_ENTITIES: EntityName[] = [
  "lists",
  "tasks",
  "task_completions",
  "task_alerts",
];

export function cacheSnapshot(
  queryClient: QueryClient,
  scope: string,
  entities: readonly EntityName[] = ALL_ENTITIES,
) {
  const out: Partial<Record<EntityName, unknown>> = {};
  for (const entity of entities) {
    out[entity] = queryClient.getQueryData(scopeKey(entity, scope));
  }
  return out;
}

export function cacheRestore(
  queryClient: QueryClient,
  scope: string,
  snapshot: ReturnType<typeof cacheSnapshot>,
) {
  for (const entity of ALL_ENTITIES) {
    if (snapshot[entity] !== undefined) {
      queryClient.setQueryData(scopeKey(entity, scope), snapshot[entity]);
    }
  }
}

export function invalidateScope(
  queryClient: QueryClient,
  scope: string,
  entities: readonly EntityName[] = ALL_ENTITIES,
) {
  for (const entity of entities) {
    void queryClient.invalidateQueries({ queryKey: scopeKey(entity, scope) });
  }
}

/** Move a guest's local rows into their new account, then clear the guest
 * cache (which is what stops this from re-running).
 *
 * Every account starts with an empty "Inbox" list, and free accounts are capped
 * at one list -- so we first drop any of the user's still-empty lists to make
 * room, then PUT the guest's lists one at a time so hitting the plan cap on a
 * later list doesn't lose the earlier ones and their tasks. Client ids are
 * kept, and every PUT is idempotent, so a half-finished run can simply be
 * retried. */
export async function migrateGuestToRemote(
  queryClient: QueryClient,
  userId: string,
) {
  const lists =
    queryClient.getQueryData<List[]>(scopeKey("lists", GUEST)) ?? [];
  const tasks =
    queryClient.getQueryData<Task[]>(scopeKey("tasks", GUEST)) ?? [];
  const completions =
    queryClient.getQueryData<TaskCompletion[]>(
      scopeKey("task_completions", GUEST),
    ) ?? [];
  const alerts =
    queryClient.getQueryData<TaskAlert[]>(scopeKey("task_alerts", GUEST)) ?? [];

  if (!lists.length && !tasks.length && !completions.length && !alerts.length) {
    return false;
  }

  // Free room by removing the account's auto-created empty lists.
  const existingLists = await fetchOwned<List>("lists");
  const existingTasks = await fetchOwned<Task>("tasks");
  const listsWithTasks = new Set(existingTasks.map((row) => row.list_id));
  for (const row of existingLists) {
    if (!listsWithTasks.has(row.id)) {
      await unwrap(
        api.deleteList({ path: { id: row.id }, query: { permanent: true } }),
      );
    }
  }

  const migratedListIds = new Set<string>();
  for (const list of lists) {
    try {
      await putRow("lists", list);
    } catch {
      // Almost always the free-plan list cap -- keep what we have.
      break;
    }
    migratedListIds.add(list.id);
  }

  const migratableTasks = tasks.filter((task) =>
    migratedListIds.has(task.list_id),
  );
  for (const task of migratableTasks) await putRow("tasks", task);

  const migratableTaskIds = new Set(migratableTasks.map((task) => task.id));
  for (const row of completions) {
    if (migratableTaskIds.has(row.task_id))
      await putRow("task_completions", row);
  }
  for (const row of alerts) await putRow("task_alerts", row);

  for (const entity of ALL_ENTITIES) {
    queryClient.setQueryData(scopeKey(entity, GUEST), []);
  }
  invalidateScope(queryClient, userId);
  return true;
}
